import React, { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { useParams, Link } from "react-router-dom";
import {
    DownloadSimple,
    ArrowLeft,
    CircleNotch,
    Certificate as CertIcon,
    EnvelopeSimple,
    PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import { downloadPNG, downloadSVG } from "@/lib/download";
import { buildCoaterzCertificateSVG } from "@/lib/coaterzCertificate";
import { CERT_TEMPLATES, getTemplate } from "@/lib/certificateTemplates";
import { api, endpoints } from "@/lib/api";

const ADMIN_TOKEN_KEY = "eviq_admin_token";

const toRoman = (num) => {
    const map = [
        [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
        [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
        [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"],
    ];
    let n = parseInt(num, 10) || 0;
    let out = "";
    for (const [v, s] of map) {
        while (n >= v) {
            out += s;
            n -= v;
        }
    }
    return out;
};

const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Personalized keepsake certificate generator.
 * Route: /certificates/:slug — slug maps to a sponsor by name.
 * Not linked from the public dashboard.
 */
const PartnerCertificate = () => {
    const { slug = "coaterz" } = useParams();
    const [sponsor, setSponsor] = useState(null);
    const [notFound, setNotFound] = useState(false);

    // Editable fields
    const today = new Date();
    const [templateId, setTemplateId] = useState("launch_partner");
    const [recipientName, setRecipientName] = useState("");
    const [subline, setSubline] = useState("");
    const [signatoryName, setSignatoryName] = useState("EVIQ Earth Initiative");
    const [signatoryTitle, setSignatoryTitle] = useState("Founding Council");
    const [issuedMonth, setIssuedMonth] = useState(today.getMonth());
    const [issuedYear, setIssuedYear] = useState(today.getFullYear());
    const [serial, setSerial] = useState("");

    const [busyPng, setBusyPng] = useState(false);
    const [busySvg, setBusySvg] = useState(false);
    const [busyEmail, setBusyEmail] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailNote, setEmailNote] = useState("");
    const [previewSvg, setPreviewSvg] = useState("");

    useEffect(() => {
        let ignore = false;
        api.get(endpoints.sponsors())
            .then((r) => {
                if (ignore) return;
                const list = r.data;
                const match = list.find(
                    (s) =>
                        s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") ===
                        slug
                );
                if (!match) {
                    setNotFound(true);
                    return;
                }
                setSponsor(match);
                setRecipientName(match.name);
                if (match.name.toLowerCase() === "coaterz") {
                    setSubline("The Gold Standard");
                    setSerial("No. 001 · LAUNCH PARTNER");
                } else {
                    setSubline(match.underwriting);
                    setSerial(`No. ${String((list.indexOf(match) || 0) + 1).padStart(3, "0")} · PARTNER`);
                }
            })
            .catch(() => !ignore && setNotFound(true));
        return () => {
            ignore = true;
        };
    }, [slug]);

    const dateLine = useMemo(
        () => `Issued this ${MONTHS[issuedMonth]}, ${toRoman(issuedYear)}`,
        [issuedMonth, issuedYear]
    );

    const buildOptions = useMemo(() => {
        const tpl = getTemplate(templateId);
        return {
            recipient: recipientName || "—",
            subline: subline || "",
            year: toRoman(issuedYear),
            dateLine,
            serial: serial || tpl.serial,
            signatoryName: signatoryName || "EVIQ Earth Initiative",
            signatoryTitle: signatoryTitle || "Founding Council",
            titleTop: tpl.titleTop,
            titleBottom: tpl.titleBottom,
            body: tpl.body,
            italicFooter: tpl.italicFooter,
        };
    }, [
        templateId,
        recipientName,
        subline,
        issuedYear,
        dateLine,
        serial,
        signatoryName,
        signatoryTitle,
    ]);

    // Rebuild preview whenever fields change
    useEffect(() => {
        let ignore = false;
        (async () => {
            const qr = QRCode.create("https://eviq.pro/app", {
                errorCorrectionLevel: "M",
            });
            const svg = buildCoaterzCertificateSVG({
                qrModules: qr.modules,
                ...buildOptions,
            });
            if (!ignore) setPreviewSvg(svg);
        })();
        return () => {
            ignore = true;
        };
    }, [buildOptions]);

    const svgToNode = (svgStr) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgStr, "image/svg+xml");
        return doc.documentElement;
    };

    const buildFresh = async () => {
        const qr = QRCode.create("https://eviq.pro/app", {
            errorCorrectionLevel: "M",
        });
        return buildCoaterzCertificateSVG({
            qrModules: qr.modules,
            ...buildOptions,
        });
    };

    const filename = (ext) => {
        const clean = (recipientName || "partner")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");
        return `eviq-${clean}-launch-partner.${ext}`;
    };

    const handlePng = async () => {
        if (busyPng) return;
        setBusyPng(true);
        try {
            const svgStr = await buildFresh();
            const svgEl = svgToNode(svgStr);
            await downloadPNG(svgEl, filename("png"), {
                width: 850,
                height: 1100,
                scale: 3,
            });
            toast.success("Certificate saved", {
                description: "2550×3300 PNG · print-ready at letter/300 DPI",
            });
        } catch (e) {
            toast.error("Could not build certificate");
        } finally {
            setBusyPng(false);
        }
    };

    const handleSvg = async () => {
        if (busySvg) return;
        setBusySvg(true);
        try {
            const svgStr = await buildFresh();
            const svgEl = svgToNode(svgStr);
            downloadSVG(svgEl, filename("svg"));
            toast.success("SVG saved — vector, infinite scale");
        } catch (e) {
            toast.error("Could not build certificate");
        } finally {
            setBusySvg(false);
        }
    };

    // -- Email --------------------------------------------------------------
    // Render the SVG preview into a canvas → PNG blob → base64, then POST
    // to /api/certificates/send. Admin token is read from localStorage
    // (same key the /social-kit gate stores).
    const svgToPngBase64 = async (svgEl) => {
        const svgData = new XMLSerializer().serializeToString(svgEl);
        const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
        const svgUrl = URL.createObjectURL(svgBlob);
        try {
            const img = new Image();
            img.crossOrigin = "anonymous";
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = svgUrl;
            });
            const scale = 2; // 1700×2200 — big enough for email, small enough to send
            const canvas = document.createElement("canvas");
            canvas.width = 850 * scale;
            canvas.height = 1100 * scale;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL("image/png");
            return dataUrl.split(",", 2)[1]; // strip "data:image/png;base64,"
        } finally {
            URL.revokeObjectURL(svgUrl);
        }
    };

    const handleEmail = async () => {
        if (busyEmail) return;
        if (!emailTo || !/.+@.+\..+/.test(emailTo)) {
            toast.error("Enter a valid recipient email");
            return;
        }
        const adminToken =
            typeof window !== "undefined" ? window.localStorage.getItem(ADMIN_TOKEN_KEY) : "";
        if (!adminToken) {
            toast.error("Admin token missing — sign in at /social-kit first");
            return;
        }
        setBusyEmail(true);
        try {
            const svgStr = await buildFresh();
            const svgEl = svgToNode(svgStr);
            const pngBase64 = await svgToPngBase64(svgEl);
            const r = await api.post("/certificates/send", {
                admin_token: adminToken,
                recipient_email: emailTo,
                partner_name: recipientName || sponsor?.name || "Partner",
                png_base64: pngBase64,
                note: emailNote || null,
            });
            if (r.data?.ok) {
                toast.success("Certificate sent", { description: `Delivered to ${emailTo}` });
                setEmailTo("");
                setEmailNote("");
            }
        } catch (e) {
            const detail =
                e?.response?.data?.detail ||
                e?.message ||
                "Something went wrong";
            toast.error("Could not send certificate", { description: detail });
        } finally {
            setBusyEmail(false);
        }
    };

    if (notFound) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#050505] text-white">
                <div className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-10 text-center">
                    <h1 className="font-editorial text-2xl">Partner not found.</h1>
                    <Link
                        to="/social-kit"
                        className="mt-4 inline-flex items-center gap-2 text-sm text-[#a3e635]"
                    >
                        <ArrowLeft size={14} /> Back to admin
                    </Link>
                </div>
            </div>
        );
    }

    const Field = ({ label, children }) => (
        <label className="flex flex-col gap-1.5">
            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                {label}
            </span>
            {children}
        </label>
    );

    const inputCls =
        "w-full rounded-md border border-[#27272a] bg-black px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-[#a3e635] focus:outline-none";

    return (
        <div
            data-testid="partner-certificate-page"
            className="min-h-screen bg-[#050505] text-white"
        >
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid #27272a",
                        color: "#fff",
                    },
                }}
            />

            <header className="border-b border-[#1f1f22] bg-black/70 backdrop-blur-xl">
                <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 py-4 sm:px-6 lg:px-10">
                    <Link
                        to="/social-kit"
                        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100"
                    >
                        <ArrowLeft size={14} /> Back to admin
                    </Link>
                    <div className="flex items-center gap-2 text-zinc-500">
                        <CertIcon size={14} weight="fill" color="#a3e635" />
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em]">
                            Private · Not on dashboard
                        </span>
                    </div>
                </div>
            </header>

            <main className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_420px] lg:px-10 lg:py-14">
                <section className="rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6 sm:p-8">
                    <span className="eviq-chip">
                        {sponsor?.name || "Partner"} · Launch Partner Keepsake
                    </span>
                    <h1 className="mt-3 font-editorial text-2xl leading-tight text-white sm:text-3xl">
                        A framed certificate, in the tradition of the Fortune 500.
                    </h1>
                    <p className="mt-2 max-w-lg text-sm text-zinc-500">
                        Live preview updates as you edit fields. Print at letter (8.5″ × 11″)
                        on matte or lightly textured stock.
                    </p>

                    <div className="mt-6 flex justify-center">
                        <div
                            data-testid="partner-cert-preview"
                            className="w-full max-w-[560px] overflow-hidden rounded-md border border-[#1f1f22] shadow-[0_20px_60px_-20px_rgba(163,230,53,0.35)] [&>svg]:block [&>svg]:h-auto [&>svg]:w-full"
                            dangerouslySetInnerHTML={{ __html: previewSvg }}
                        />
                    </div>
                </section>

                <aside className="flex h-fit flex-col gap-5 rounded-lg border border-[#1f1f22] bg-[#0a0a0a] p-6">
                    <div>
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            Personalize
                        </span>
                        <h2 className="mt-2 font-editorial text-xl text-white">Fields</h2>
                    </div>

                    <Field label="Template">
                        <div className="flex flex-wrap gap-2">
                            {CERT_TEMPLATES.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    data-testid={`cert-template-${t.id}`}
                                    onClick={() => setTemplateId(t.id)}
                                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ${
                                        templateId === t.id
                                            ? "border-[#a3e635] bg-[#0a0f05] text-[#a3e635]"
                                            : "border-[#27272a] bg-black text-zinc-300 hover:border-[#3a3a3f]"
                                    }`}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <Field label="Recipient">
                        <input
                            data-testid="cert-field-recipient"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            placeholder="Partner name"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Sub-line">
                        <input
                            data-testid="cert-field-subline"
                            value={subline}
                            onChange={(e) => setSubline(e.target.value)}
                            placeholder="e.g. The Gold Standard"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Signatory name">
                        <input
                            data-testid="cert-field-signatory"
                            value={signatoryName}
                            onChange={(e) => setSignatoryName(e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Signatory title">
                        <input
                            data-testid="cert-field-signatory-title"
                            value={signatoryTitle}
                            onChange={(e) => setSignatoryTitle(e.target.value)}
                            className={inputCls}
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Issued month">
                            <select
                                data-testid="cert-field-month"
                                value={issuedMonth}
                                onChange={(e) => setIssuedMonth(Number(e.target.value))}
                                className={inputCls}
                            >
                                {MONTHS.map((m, i) => (
                                    <option key={m} value={i}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Issued year">
                            <input
                                data-testid="cert-field-year"
                                type="number"
                                min="2024"
                                max="2100"
                                value={issuedYear}
                                onChange={(e) => setIssuedYear(Number(e.target.value))}
                                className={inputCls}
                            />
                        </Field>
                    </div>
                    <Field label="Serial line (bottom)">
                        <input
                            data-testid="cert-field-serial"
                            value={serial}
                            onChange={(e) => setSerial(e.target.value)}
                            className={inputCls}
                        />
                    </Field>

                    <div className="mt-2 flex flex-col gap-3 border-t border-[#1f1f22] pt-4">
                        <button
                            type="button"
                            data-testid="coaterz-cert-download-png"
                            onClick={handlePng}
                            disabled={busyPng}
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#a3e635] px-5 py-3 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                        >
                            {busyPng ? (
                                <CircleNotch size={14} className="animate-spin" />
                            ) : (
                                <DownloadSimple size={14} weight="bold" />
                            )}
                            Download PNG · Print-ready
                        </button>
                        <button
                            type="button"
                            data-testid="coaterz-cert-download-svg"
                            onClick={handleSvg}
                            disabled={busySvg}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#27272a] bg-black px-5 py-3 text-sm text-zinc-200 hover:border-[#3a3a3f] disabled:opacity-60"
                        >
                            {busySvg ? (
                                <CircleNotch size={14} className="animate-spin" />
                            ) : (
                                <DownloadSimple size={14} weight="bold" />
                            )}
                            Download SVG · Vector
                        </button>
                    </div>

                    <div className="mt-2 flex flex-col gap-3 border-t border-[#1f1f22] pt-4">
                        <div className="flex items-center gap-2 text-zinc-500">
                            <EnvelopeSimple size={14} weight="fill" color="#a3e635" />
                            <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em]">
                                Email this certificate
                            </span>
                        </div>
                        <Field label="Recipient email">
                            <input
                                data-testid="cert-email-to"
                                type="email"
                                value={emailTo}
                                onChange={(e) => setEmailTo(e.target.value)}
                                placeholder="partner@company.com"
                                className={inputCls}
                            />
                        </Field>
                        <Field label="Personal note (optional)">
                            <textarea
                                data-testid="cert-email-note"
                                value={emailNote}
                                onChange={(e) => setEmailNote(e.target.value)}
                                rows={2}
                                placeholder="Add a short handwritten-feeling note…"
                                className={`${inputCls} resize-none`}
                            />
                        </Field>
                        <button
                            type="button"
                            data-testid="cert-email-send"
                            onClick={handleEmail}
                            disabled={busyEmail}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#a3e635] bg-[#0a0f05] px-5 py-3 text-sm font-medium text-[#a3e635] hover:bg-[#141a09] disabled:opacity-60"
                        >
                            {busyEmail ? (
                                <CircleNotch size={14} className="animate-spin" />
                            ) : (
                                <PaperPlaneTilt size={14} weight="bold" />
                            )}
                            Send certificate
                        </button>
                        <p className="text-[10px] leading-relaxed text-zinc-600">
                            Sent from EVIQ Earth Initiative via Resend. Recipient gets the
                            PNG as an attachment plus an inline preview.
                        </p>
                    </div>
                </aside>
            </main>
        </div>
    );
};

export default PartnerCertificate;
